import { useEffect, useState } from 'react';

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

/** Draws `img` turned clockwise by `quarterTurns` × 90° and returns it as a blob: URL. */
const rotateToObjectUrl = async (img, quarterTurns) => {
  const sideways = quarterTurns % 2 === 1;
  const canvas = document.createElement('canvas');
  canvas.width = sideways ? img.naturalHeight : img.naturalWidth;
  canvas.height = sideways ? img.naturalWidth : img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((quarterTurns * Math.PI) / 2);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))));
  });
  return URL.createObjectURL(blob);
};

const NONE = { key: null, source: null, url: null };

/**
 * A display URL for `url` turned clockwise by `quarterTurns` × 90°, so a credential uploaded
 * sideways can be read without turning your head. Display only: the uploaded file is never
 * changed. The rotation is drawn into the pixels rather than applied with CSS, because the
 * zoom view sets its own transform and sizes the image from its natural dimensions.
 *
 * Returns `url` unchanged when there is no rotation, or if it cannot be drawn (e.g. a
 * cross-origin image taints the canvas). While a new rotation is being drawn it keeps
 * returning the previous rotation of the same image, so the image never flashes upright.
 */
export const useRotatedImageUrl = (url, quarterTurns) => {
  const turns = ((quarterTurns % 4) + 4) % 4;
  const key = `${url}|${turns}`;
  // Tagged with the image and rotation it was drawn for, so it is never shown for another.
  const [rotated, setRotated] = useState(NONE);

  useEffect(() => {
    if (!url || turns === 0) {
      setRotated((current) => (current.url ? NONE : current));
      return undefined;
    }

    let cancelled = false;
    loadImage(url)
      .then((img) => rotateToObjectUrl(img, turns))
      .then((objectUrl) => {
        if (cancelled) URL.revokeObjectURL(objectUrl);
        else setRotated({ key: `${url}|${turns}`, source: url, url: objectUrl });
      })
      .catch(() => {
        // Keep showing the original; rotation is a convenience, not a requirement.
      });

    return () => {
      cancelled = true;
    };
  }, [url, turns]);

  // Release each rotated copy once it is replaced or dropped, and the last one on unmount.
  useEffect(() => {
    const { url: objectUrl } = rotated;
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rotated]);

  if (!url || turns === 0) return url;
  if (rotated.key === key || rotated.source === url) return rotated.url;
  return url;
};

export default useRotatedImageUrl;
